import {
  authenticateUser,
  checkUsernameExists,
  clearAuthSession,
  fetchJwt,
  fetchSwitchedJwt,
  logoutUser,
  redirectUserToTid,
  setAuthCode,
  setAuthConfig,
  setAuthErrorMap,
  setAuthErrorMessage,
  setAuthState,
  setAuthTextMap,
  switchAccounts,
  verifyAuthSession,
} from './auth.actions';
import {
  authConfigSnapshot,
  authStoreSnapshot,
  authUserSnapshot,
  isOriginalLogin,
  resetAuthStoreOnLogout,
  trackAuthRequests,
  updateAuthCode,
  updateAuthConfig,
  updateAuthErrorMap,
  updateAuthErrorMessage,
  updateAuthState,
  updateAuthTextMap,
  updateAuthUser,
  updateCheckedForExistingSession,
  updateJwt,
  updateSsoAccounts,
  updateTidRedirectUrl,
  updateUsernameExists,
} from './auth.repository';
import { lookUpAuthError } from '../utils/look-up-auth-error';
import { parseLocalStorageItem } from '../utils/parse-local-storage-item';
import { debugMessage } from '../utils/debug-message';
import {
  TID_ID_TOKEN_KEY,
  TID_ACCESS_TOKEN_KEY,
  TID_USERNAME_KEY,
  ID_TOKEN_SUFFIX,
  ACCESS_TOKEN_SUFFIX,
} from '../models/local-storage-keys';
import { switchMap } from 'rxjs';
import {
  createEffect,
  dispatch,
  ofType,
  registerEffects,
  tapResult,
} from '@ngneat/effects';
import {
  AccountRole,
  AuthenticatedUser,
  AuthwsApi,
  Jwt,
  NormalizedResponse,
  redirectToTid,
  trimbleIdentityEnvironment,
} from '@tmx/data-access';

const EXPIRED_ACCOUNT = 'FailedToLoginAccountExpired';

function handleAuthUser(user: AuthenticatedUser) {
  for (const key in localStorage) {
    if (key === TID_USERNAME_KEY) {
      localStorage.removeItem(key);
    }

    if (
      key.includes(ID_TOKEN_SUFFIX) &&
      key !== TID_ID_TOKEN_KEY(user.userId)
    ) {
      localStorage.removeItem(key);
    }

    if (
      key.includes(ACCESS_TOKEN_SUFFIX) &&
      key !== TID_ACCESS_TOKEN_KEY(user.userId)
    ) {
      localStorage.removeItem(key);
    }
  }
  if (typeof user.idToken === 'string') {
    localStorage.setItem(TID_ID_TOKEN_KEY(user.userId), user.idToken);
  }

  if (typeof user.accessToken === 'string') {
    localStorage.setItem(TID_ACCESS_TOKEN_KEY(user.userId), user.accessToken);
  }

  updateAuthUser(user);
  updateCheckedForExistingSession(true);

  const state = authStoreSnapshot().authState.state;

  if (state === 'authenticating-user' || isOriginalLogin()) {
    dispatch(fetchJwt({ authenticationToken: user.authToken }));
    return;
  }

  if (state === 'switching-account') {
    dispatch(fetchSwitchedJwt({ targetAccountId: user.accountId }));
  }
}

function handleAuthError(status: string) {
  switch (status) {
    case 'LoginNotExist':
      dispatch(setAuthErrorMessage({ errorType: 'loginNotExist' }));
      break;
    case 'Unauthorized':
      dispatch(setAuthErrorMessage({ errorType: 'unauthorized' }));
      break;
    case 'FailedToLoginAccountExpired':
      dispatch(setAuthErrorMessage({ errorType: 'accountExpired' }));
      break;
    case 'FailedToValidateTrimbleIdentityToken':
      dispatch(setAuthErrorMessage({ errorType: 'tidTokenExpired' }));
      break;
    case 'FailedToLoginNotAuthorized':
      dispatch(setAuthErrorMessage({ errorType: 'notLicensedForProduct' }));
      break;

    default:
      dispatch(setAuthErrorMessage({ errorType: 'genericError' }));
      break;
  }
}

const setAuthConfig$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAuthConfig),
    tapResult(({ authConfig }) => {
      updateAuthConfig(authConfig);
    })
  )
);

const setAuthTextMap$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAuthTextMap),
    tapResult(({ authTextMap: authText }) => {
      updateAuthTextMap(authText);
    })
  )
);

const setAuthErrortMap$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAuthErrorMap),
    tapResult(({ authErrorMap }) => {
      updateAuthErrorMap(authErrorMap);
    })
  )
);

const checkUsernameExists$ = createEffect((actions) =>
  actions.pipe(
    ofType(checkUsernameExists),
    switchMap(({ email }) =>
      AuthwsApi.postMigrationStatus(email).pipe(
        trackAuthRequests(['username-exists']),
        tapResult(({ exists }) => {
          updateUsernameExists(exists);
        })
      )
    )
  )
);

const setAuthErrorMessage$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAuthErrorMessage),
    tapResult(({ errorType }) => {
      if (errorType === null) {
        updateAuthErrorMessage(errorType);
      } else {
        updateAuthErrorMessage(lookUpAuthError(errorType));
      }
    })
  )
);

const redirectUserToTid$ = createEffect((actions) =>
  actions.pipe(
    ofType(redirectUserToTid),
    tapResult(({ username, skipLoginPage }) => {
      const {
        trimbleCloudAppName,
        trimbleCloudClientId,
        callbackUrl,
        stateParams,
        identityProvider,
      } = authConfigSnapshot().trimbleIdentity;
      let email: string | null = null;
      if (typeof username === 'string') {
        localStorage.setItem(TID_USERNAME_KEY, JSON.stringify(username));
        email = username;
      } else {
        email = authConfigSnapshot().settings.enableExperimentalWorkflow
          ? ''
          : parseLocalStorageItem<string>(TID_USERNAME_KEY);
      }

      redirectToTid({
        client_id: trimbleCloudClientId,
        applicationName: trimbleCloudAppName,
        redirectUrl: callbackUrl,
        prompt: (skipLoginPage ?? false) ? 'none' : 'login',
        email: email ?? '',
        state: stateParams,
        identityProvider,
      });
    })
  )
);

const setAuthCode$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAuthCode),
    tapResult(({ authCode }) => {
      updateAuthCode(authCode);
    })
  )
);

const authenticateUser$ = createEffect((actions) =>
  actions.pipe(
    ofType(authenticateUser),
    switchMap(({ authCode, account }) => {
      const { trimbleIdentity } = authConfigSnapshot();
      return AuthwsApi.postTidAuthenticate(
        authCode,
        {
          redirectUrl: trimbleIdentity.callbackUrl,
          applicationName: trimbleIdentity.trimbleMapsAppName,
          trimbleApplication: trimbleIdentity.trimbleCloudAppName,
        },
        account
      ).pipe(
        trackAuthRequests(['authenticate-user']),
        tapResult(
          (authResponse: Record<string, unknown>) => {
            if (authResponse['status'] === EXPIRED_ACCOUNT) {
              dispatch(setAuthErrorMessage({ errorType: 'accountExpired' }));
              updateSsoAccounts([]);
            }

            if (Array.isArray(authResponse['accounts'])) {
              const accounts = authResponse['accounts'] as AccountRole[];
              updateSsoAccounts(accounts.map(({ accountName }) => accountName));
            }

            const isAuthenticated =
              typeof authResponse['authToken'] === 'string';

            if (isAuthenticated) {
              const user = authResponse as AuthenticatedUser;
              handleAuthUser(user);
            }
          },
          (error: NormalizedResponse) => {
            handleAuthError(
              !Array.isArray(error.error) &&
                typeof error.error['status'] === 'string'
                ? error.error['status']
                : ''
            );
          }
        )
      );
    })
  )
);

const setAuthState$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAuthState),
    tapResult(({ authState }) => {
      debugMessage('Auth State', authState.state);
      updateAuthState(authState);

      const { eventHandlers } = authConfigSnapshot();
      if (authState.state === 'authenticated') {
        eventHandlers.onAuthenticated(authState);
      }

      if (authState.state === 'authenticating-user') {
        eventHandlers.onAuthenticating(authState);
      }

      if (authState.state === 'logged-out') {
        eventHandlers.onLoggedOut();
      }

      if (authState.state === 'logging-out') {
        eventHandlers.onLoggingOut();
      }

      if (authState.state === 'switching-account') {
        eventHandlers.onSwitchingAccount();
      }
    })
  )
);

const fetchJwt$ = createEffect((actions) =>
  actions.pipe(
    ofType(fetchJwt),
    switchMap(({ authenticationToken }) =>
      AuthwsApi.postToken({ authenticationToken }).pipe(
        trackAuthRequests(['fetch-jwt']),
        tapResult((jwt) => {
          updateJwt(jwt);
          dispatch(
            setAuthState({
              authState: {
                state: 'authenticated',
                user: authUserSnapshot(),
                jwt,
                isOriginalLogin: isOriginalLogin(),
              },
            })
          );
        })
      )
    )
  )
);

const verifyAuthSession$ = createEffect((actions) =>
  actions.pipe(
    ofType(verifyAuthSession),
    switchMap(({ appName }) =>
      AuthwsApi.postVerify(appName).pipe(
        trackAuthRequests(['authenticate-user']),
        tapResult(
          (user) => {
            handleAuthUser(user);
          },
          () => {
            dispatch(
              setAuthState({
                authState: {
                  state: 'idle',
                },
              })
            );
          }
        )
      )
    )
  )
);

const switchAccounts$ = createEffect((actions) =>
  actions.pipe(
    ofType(switchAccounts),
    switchMap(({ authToken, alias, product }) =>
      AuthwsApi.postSecurityAccountSwitch({ authToken, alias, product }).pipe(
        trackAuthRequests(['authenticate-user']),
        tapResult((switchedAccount) => {
          handleAuthUser(switchedAccount);
        })
      )
    )
  )
);

const fetchSwitchedJwt$ = createEffect((actions) =>
  actions.pipe(
    ofType(fetchSwitchedJwt),
    switchMap(({ targetAccountId }) =>
      AuthwsApi.getIdentityTokenSwitchaccount(targetAccountId).pipe(
        trackAuthRequests(['fetch-jwt']),
        tapResult((jwt) => {
          const newJwt: Jwt = {
            ...jwt,
            refresh_token: jwt.refresh_token ?? '',
          };
          updateJwt(newJwt);
          dispatch(
            setAuthState({
              authState: {
                state: 'authenticated',
                user: authUserSnapshot(),
                jwt: newJwt,
                isOriginalLogin: isOriginalLogin(),
              },
            })
          );
        })
      )
    )
  )
);

const logoutUser$ = createEffect((actions) =>
  actions.pipe(
    ofType(logoutUser),
    tapResult(() => {
      dispatch(
        setAuthState({
          authState: {
            state: 'logging-out',
          },
        })
      );

      const { userId } = authUserSnapshot();

      const idToken = localStorage.getItem(TID_ID_TOKEN_KEY(userId));
      let url = `${
        trimbleIdentityEnvironment.baseUrl
      }/oauth/logout?${new URLSearchParams([
        ['post_logout_redirect_uri', `${location.origin}/auth/login`],
      ]).toString()}`;

      if (idToken !== null) {
        const idTokenParams = new URLSearchParams([['id_token_hint', idToken]]);
        url += `&${idTokenParams.toString()}`;
      }
      updateTidRedirectUrl(url);
    }),
    switchMap(() => {
      const { userId } = authUserSnapshot();

      return AuthwsApi.postLogout(authUserSnapshot().authToken).pipe(
        tapResult(() => {
          localStorage.removeItem(TID_ID_TOKEN_KEY(userId));
          localStorage.removeItem(TID_ACCESS_TOKEN_KEY(userId));
          dispatch(setAuthState({ authState: { state: 'logged-out' } }));
          dispatch(clearAuthSession());
        })
      );
    })
  )
);

const clearAuthSession$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearAuthSession),
    tapResult(() => {
      resetAuthStoreOnLogout();
    })
  )
);

const effects = [
  setAuthConfig$,
  setAuthTextMap$,
  setAuthErrortMap$,
  checkUsernameExists$,
  setAuthErrorMessage$,
  redirectUserToTid$,
  setAuthCode$,
  authenticateUser$,
  setAuthState$,
  fetchJwt$,
  verifyAuthSession$,
  switchAccounts$,
  fetchSwitchedJwt$,
  logoutUser$,
  clearAuthSession$,
];

export function registerAuthEffects() {
  registerEffects(effects);
}
